import { createCanvasSceneRenderer } from "./canvas-renderer.js";
import { createDomSceneRenderer } from "./dom-renderer.js";
import {
  RENDERER_KINDS,
  type RendererFailure,
  type RendererFailureCode,
  type RendererFailurePhase,
  type RendererFramePolicy,
  type RendererObservation,
  type SceneRenderer,
  type SceneRendererKind,
} from "./renderer-contract.js";
import {
  assertScenePlanV1,
  computeScenePlanSignature,
  type ScenePlan,
  type ScenePlanSignature,
  type ScenePlanUnsigned,
} from "./scene-plan.js";
import { createThreeSceneRenderer } from "./three-renderer.js";

const LOW_POWER_FRAME_INTERVAL_MS = 1000 / 30;
const MAX_CLEANUP_ATTEMPTS = 2;
const FALLBACK_ORDER: Readonly<Record<SceneRendererKind, readonly SceneRendererKind[]>> =
  Object.freeze({
    three: Object.freeze(["canvas", "dom"] as const),
    canvas: Object.freeze(["dom"] as const),
    dom: Object.freeze([] as const),
  });

export type RendererHostFactory = (
  root: HTMLElement,
  initialScenePlan: ScenePlan,
) => SceneRenderer;

export interface RendererHostFactories {
  readonly three: RendererHostFactory;
  readonly canvas: RendererHostFactory;
  readonly dom: RendererHostFactory;
}

export interface RendererHostOptions {
  readonly root: HTMLElement;
  readonly scenePlan: ScenePlan;
  readonly preferred?: SceneRendererKind;
  readonly search?: string;
  readonly reducedMotion?: boolean;
  readonly lowPower?: boolean;
  readonly factories?: Partial<RendererHostFactories>;
  readonly requestAnimationFrame?: (callback: FrameRequestCallback) => number;
  readonly cancelAnimationFrame?: (handle: number) => void;
  readonly visibilityDocument?: Pick<Document, "hidden" | "addEventListener" | "removeEventListener"> | null;
  readonly pageLifecycleTarget?: Pick<Window, "addEventListener" | "removeEventListener"> | null;
  readonly boundsTarget?: Pick<EventTarget, "addEventListener" | "removeEventListener"> | null;
  readonly createBoundsObserver?: ((listener: () => void) => Pick<ResizeObserver, "observe" | "disconnect">) | null;
  readonly onFrame?: (nowMs: number) => void;
  readonly onKindChange?: (kind: SceneRendererKind | null) => void;
}

export interface RendererHostFailure {
  readonly kind: "host";
  readonly sourceKind: SceneRendererKind | null;
  readonly phase: RendererFailurePhase;
  readonly code: RendererFailureCode;
  readonly message: string;
  readonly recoverable: false;
  readonly cause?: RendererFailure;
}

export interface RendererHostDiagnostics {
  readonly kind: SceneRendererKind | null;
  readonly signature: ScenePlanSignature | null;
  readonly framePolicy: RendererFramePolicy;
  readonly canvasCount: number;
  readonly activeSubscriptions: 0 | 1;
  readonly activeAdapterCount: 0 | 1;
  readonly attemptedKinds: readonly SceneRendererKind[];
  readonly lowPower: boolean;
  readonly reducedMotion: boolean;
  readonly cleanupPending: boolean;
  readonly lastFailure: RendererFailure | null;
  readonly terminalFailure: RendererHostFailure | null;
  readonly destroyed: boolean;
  readonly observation: RendererObservation | null;
  readonly retiredObservations: readonly RendererObservation[];
}

export interface RendererHost {
  apply(nextScenePlan: ScenePlan): void;
  setReducedMotion(reduced: boolean): void;
  diagnostics(): RendererHostDiagnostics;
  destroy(): void;
}

interface AdapterRecord {
  readonly adapter: SceneRenderer;
  readonly token: number;
  readonly failures: RendererFailure[];
  framePolicy: RendererFramePolicy;
  unsubscribeFailure: (() => void) | null;
  frameHandle: number | null;
  frameGeneration: number;
  cleaning: boolean;
}

interface BufferedOperation {
  readonly record: AdapterRecord;
  readonly failures: RendererFailure[];
}

const DEFAULT_FACTORIES: RendererHostFactories = Object.freeze({
  three(root: HTMLElement, initialScenePlan: ScenePlan): SceneRenderer {
    return createThreeSceneRenderer(root, {}, initialScenePlan);
  },
  canvas(root: HTMLElement): SceneRenderer {
    return createCanvasSceneRenderer(root);
  },
  dom(root: HTMLElement): SceneRenderer {
    return createDomSceneRenderer(root);
  },
});

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

function snapshotScenePlan(scenePlan: ScenePlan): ScenePlan {
  assertScenePlanV1(scenePlan);
  return deepFreeze(structuredClone(scenePlan));
}

export function deriveLowPowerScenePlan(scenePlan: ScenePlan): ScenePlan {
  const clone = snapshotScenePlan(scenePlan);
  const { signature: _canonicalSignature, ...canonicalUnsigned } = clone;
  const streakCount = Math.max(1, Math.floor(clone.rain.primitives.length / 2));
  const rippleCount = Math.max(1, Math.floor(clone.ripples.primitives.length / 2));
  const reducedUnsigned: ScenePlanUnsigned = {
    ...canonicalUnsigned,
    rain: {
      ...clone.rain,
      primitives: clone.rain.primitives.slice(0, streakCount),
    },
    ripples: {
      ...clone.ripples,
      primitives: clone.ripples.primitives.slice(0, rippleCount),
    },
  };
  return snapshotScenePlan({
    ...reducedUnsigned,
    signature: computeScenePlanSignature(reducedUnsigned),
  });
}

function queryRenderer(search: string): SceneRendererKind | null {
  try {
    const candidate = new URLSearchParams(search).get("renderer");
    return RENDERER_KINDS.find((kind) => kind === candidate) ?? null;
  } catch {
    return null;
  }
}

function currentSearch(): string {
  try {
    return globalThis.location?.search ?? "";
  } catch {
    return "";
  }
}

function prefersReducedMotion(): boolean {
  try {
    return typeof globalThis.matchMedia === "function"
      && globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function defaultRequestFrame(callback: FrameRequestCallback): number {
  if (typeof globalThis.requestAnimationFrame !== "function") {
    throw new TypeError("Animated renderer host requires requestAnimationFrame");
  }
  return globalThis.requestAnimationFrame(callback);
}

function defaultCancelFrame(handle: number): void {
  if (typeof globalThis.cancelAnimationFrame === "function") {
    globalThis.cancelAnimationFrame(handle);
  }
}

function rendererFailure(value: unknown): RendererFailure | null {
  try {
    if (value === null || (typeof value !== "object" && typeof value !== "function")) return null;
    const direct = value as Partial<RendererFailure> & { readonly failure?: unknown };
    const candidate = direct.failure ?? direct;
    if (candidate === null || typeof candidate !== "object") return null;
    const failure = candidate as Partial<RendererFailure>;
    if (!RENDERER_KINDS.some((kind) => kind === failure.kind)
        || typeof failure.phase !== "string"
        || typeof failure.code !== "string"
        || typeof failure.message !== "string"
        || typeof failure.recoverable !== "boolean") {
      return null;
    }
    return failure as RendererFailure;
  } catch {
    return null;
  }
}

function hostFailure(
  sourceKind: SceneRendererKind | null,
  phase: RendererFailurePhase,
  code: RendererFailureCode,
  message: string,
  cause?: RendererFailure,
): RendererHostFailure {
  return Object.freeze(cause === undefined
    ? { kind: "host", sourceKind, phase, code, message, recoverable: false }
    : { kind: "host", sourceKind, phase, code, message, recoverable: false, cause });
}

function selectFirstKind(
  reducedMotion: boolean,
  preferred: SceneRendererKind | undefined,
  queryPreferred: SceneRendererKind | null,
  lowPower: boolean,
): SceneRendererKind {
  if (reducedMotion || preferred === "dom" || queryPreferred === "dom") return "dom";
  if (lowPower) return "canvas";
  return preferred ?? queryPreferred ?? "three";
}

export function mountRendererHost(options: RendererHostOptions): RendererHost {
  const root = options.root;
  const onFrame = options.onFrame;
  const factories: RendererHostFactories = { ...DEFAULT_FACTORIES, ...options.factories };
  const lowPower = options.lowPower === true;
  let reducedMotion = options.reducedMotion ?? prefersReducedMotion();
  const queryPreferred = queryRenderer(options.search ?? currentSearch());
  const forcedDom = options.preferred === "dom" || queryPreferred === "dom";
  const baselineKind = selectFirstKind(false, options.preferred, queryPreferred, lowPower);
  const firstKind = reducedMotion || forcedDom ? "dom" : baselineKind;
  const requestFrame = options.requestAnimationFrame ?? defaultRequestFrame;
  const cancelFrame = options.cancelAnimationFrame ?? defaultCancelFrame;
  const visibilityDocument = options.visibilityDocument === undefined
    ? (typeof document === "undefined" ? null : document)
    : options.visibilityDocument;
  const pageLifecycleTarget = options.pageLifecycleTarget === undefined
    ? (typeof window === "undefined" ? null : window)
    : options.pageLifecycleTarget;
  const boundsTarget = options.boundsTarget === undefined
    ? root.ownerDocument?.defaultView ?? (typeof window === "undefined" ? null : window)
    : options.boundsTarget;
  const createBoundsObserver = options.createBoundsObserver === undefined
    ? (typeof ResizeObserver === "undefined"
      ? null
      : (listener: () => void): ResizeObserver => new ResizeObserver(listener))
    : options.createBoundsObserver;
  const attemptedKinds = new Set<SceneRendererKind>();
  const retiredObservations: RendererObservation[] = [];
  const retiredTokens = new Set<number>();

  let appliedPlan: ScenePlan | null = null;
  let active: AdapterRecord | null = null;
  let activating: AdapterRecord | null = null;
  let applying: BufferedOperation | null = null;
  let framing: BufferedOperation | null = null;
  let cleanupPending: AdapterRecord | null = null;
  let terminalFailure: RendererHostFailure | null = null;
  let lastFailure: RendererFailure | null = null;
  let nextToken = 1;
  let lastLowPowerFrameMs: number | null = null;
  let destroyRequested = false;
  let destroyed = false;
  let recovering = false;
  let lifecyclePaused = visibilityDocument?.hidden ?? false;
  let visibilityListenerAttached = false;
  let pageHideListenerAttached = false;
  let pageShowListenerAttached = false;
  let boundsListenerAttached = false;
  let boundsObserver: Pick<ResizeObserver, "observe" | "disconnect"> | null = null;

  const pauseFrames = (): void => {
    lifecyclePaused = true;
    lastLowPowerFrameMs = null;
    const record = active ?? activating;
    if (record !== null && !cancelRecordFrame(record)) {
      setTerminal(record.adapter.kind, "frame", "resource", "Renderer host could not pause its frame subscription");
    }
  };
  const resumeFrames = (): void => {
    lifecyclePaused = visibilityDocument?.hidden ?? false;
    lastLowPowerFrameMs = null;
    if (destroyRequested || lifecyclePaused || terminalFailure !== null
        || cleanupPending !== null || active === null) return;
    if (!scheduleFrame(active)) {
      const record = active;
      const cleaned = cleanupRecord(record);
      setTerminal(
        record.adapter.kind,
        cleaned ? "frame" : "destroy",
        cleaned ? "resource" : "cleanup",
        "Renderer host could not resume its frame subscription",
      );
    }
  };
  const onVisibilityChange = (): void => {
    if (visibilityDocument?.hidden) pauseFrames();
    else resumeFrames();
  };
  const onPageHide = (): void => pauseFrames();
  const onPageShow = (): void => resumeFrames();
  const onBoundsChange = (): void => {
    const record = active;
    if (record === null || record.adapter.kind !== "dom" || appliedPlan === null
        || destroyRequested || terminalFailure !== null || cleanupPending !== null) return;
    const operation: BufferedOperation = { record, failures: [] };
    applying = operation;
    let thrown: unknown;
    try {
      record.adapter.apply(appliedPlan);
    } catch (cause) {
      thrown = cause;
    } finally {
      applying = null;
    }
    const failure = operation.failures[0] ?? rendererFailure(thrown);
    if (failure === null && safeDiagnostics(record)?.signature === appliedPlan.signature) return;
    const cleaned = cleanupRecord(record);
    setTerminal(
      record.adapter.kind,
      cleaned ? "apply" : "destroy",
      cleaned ? "resource" : "cleanup",
      "Static renderer failed to reapply ScenePlan after bounds changed",
      failure ?? undefined,
    );
  };

  function addLifecycleListeners(): void {
    try {
      if (visibilityDocument !== null && !visibilityListenerAttached) {
        visibilityListenerAttached = true;
        visibilityDocument.addEventListener("visibilitychange", onVisibilityChange);
      }
      if (pageLifecycleTarget !== null && !pageHideListenerAttached) {
        pageHideListenerAttached = true;
        pageLifecycleTarget.addEventListener("pagehide", onPageHide);
      }
      if (pageLifecycleTarget !== null && !pageShowListenerAttached) {
        pageShowListenerAttached = true;
        pageLifecycleTarget.addEventListener("pageshow", onPageShow);
      }
      if (createBoundsObserver !== null && boundsObserver === null) {
        boundsObserver = createBoundsObserver(onBoundsChange);
        boundsObserver.observe(root);
      } else if (createBoundsObserver === null && boundsTarget !== null && !boundsListenerAttached) {
        boundsListenerAttached = true;
        boundsTarget.addEventListener("resize", onBoundsChange);
      }
    } catch (cause) {
      removeLifecycleListeners();
      throw cause;
    }
  }

  function removeLifecycleListeners(): unknown {
    let firstFailure: unknown;
    const attempt = (attached: boolean, remove: () => void, clear: () => void): void => {
      if (!attached) return;
      try {
        remove();
        clear();
      } catch (cause) {
        firstFailure ??= cause;
      }
    };
    attempt(boundsObserver !== null, () => boundsObserver?.disconnect(), () => {
      boundsObserver = null;
    });
    attempt(boundsListenerAttached, () => boundsTarget?.removeEventListener("resize", onBoundsChange), () => {
      boundsListenerAttached = false;
    });
    attempt(pageShowListenerAttached, () => pageLifecycleTarget?.removeEventListener("pageshow", onPageShow), () => {
      pageShowListenerAttached = false;
    });
    attempt(pageHideListenerAttached, () => pageLifecycleTarget?.removeEventListener("pagehide", onPageHide), () => {
      pageHideListenerAttached = false;
    });
    attempt(visibilityListenerAttached, () => visibilityDocument?.removeEventListener("visibilitychange", onVisibilityChange), () => {
      visibilityListenerAttached = false;
    });
    return firstFailure;
  }

  function preparePlan(scenePlan: ScenePlan): ScenePlan {
    return lowPower ? deriveLowPowerScenePlan(scenePlan) : snapshotScenePlan(scenePlan);
  }

  function nextFallback(kind: SceneRendererKind): SceneRendererKind | null {
    return FALLBACK_ORDER[kind].find((candidate) => !attemptedKinds.has(candidate)) ?? null;
  }

  function safeDiagnostics(record: AdapterRecord): ReturnType<SceneRenderer["diagnostics"]> | null {
    try {
      return record.adapter.diagnostics();
    } catch {
      return null;
    }
  }

  function resourcesReadyForUnsubscribe(record: AdapterRecord): boolean {
    const diagnostics = safeDiagnostics(record);
    if (diagnostics === null) return false;
    const allowedFailureSubscription = record.unsubscribeFailure === null ? 0 : 1;
    return (diagnostics.cleanupComplete === undefined || diagnostics.cleanupComplete === true)
      && diagnostics.canvasCount === 0
      && diagnostics.objectCount === 0
      && diagnostics.activeSubscriptions <= allowedFailureSubscription;
  }

  function resourcesAreZero(record: AdapterRecord): boolean {
    const diagnostics = safeDiagnostics(record);
    return diagnostics !== null
      && (diagnostics.cleanupComplete === undefined || diagnostics.cleanupComplete === true)
      && diagnostics.canvasCount === 0
      && diagnostics.objectCount === 0
      && diagnostics.activeSubscriptions === 0;
  }

  function cancelRecordFrame(record: AdapterRecord): boolean {
    if (record.frameHandle === null) return true;
    const handle = record.frameHandle;
    record.frameGeneration += 1;
    record.frameHandle = null;
    try {
      cancelFrame(handle);
      return true;
    } catch {
      return false;
    }
  }

  function cleanupRecord(record: AdapterRecord): boolean {
    if (active === record) active = null;
    if (activating === record) activating = null;
    record.cleaning = true;
    cleanupPending = record;
    const frameCancelled = cancelRecordFrame(record);

    let cleanAttempt = false;
    for (let attempt = 0; attempt < MAX_CLEANUP_ATTEMPTS; attempt += 1) {
      const failureCountBeforeAttempt = record.failures.length;
      try {
        record.adapter.destroy();
      } catch (cause) {
        const failure = rendererFailure(cause);
        if (failure !== null) record.failures.push(failure);
      }
      const cleanupFailureEmitted = record.failures
        .slice(failureCountBeforeAttempt)
        .some((failure) => failure.phase === "destroy" || failure.code === "cleanup");
      if (!cleanupFailureEmitted && resourcesReadyForUnsubscribe(record)) {
        cleanAttempt = true;
        break;
      }
    }

    if (!frameCancelled || !cleanAttempt || !resourcesReadyForUnsubscribe(record)) {
      cleanupPending = record;
      return false;
    }

    if (record.unsubscribeFailure !== null) {
      try {
        record.unsubscribeFailure();
        record.unsubscribeFailure = null;
      } catch {
        cleanupPending = record;
        return false;
      }
    }
    if (!resourcesAreZero(record)) {
      cleanupPending = record;
      return false;
    }

    if (!retiredTokens.has(record.token)) {
      const retired = safeDiagnostics(record)?.observation;
      if (retired !== undefined) retiredObservations.push(retired);
      retiredTokens.add(record.token);
    }

    record.cleaning = false;
    if (cleanupPending === record) cleanupPending = null;
    return true;
  }

  function setTerminal(
    sourceKind: SceneRendererKind | null,
    phase: RendererFailurePhase,
    code: RendererFailureCode,
    message: string,
    cause?: RendererFailure,
  ): void {
    terminalFailure = hostFailure(sourceKind, phase, code, message, cause);
    options.onKindChange?.(null);
  }

  function listenerFor(record: AdapterRecord): (failure: RendererFailure) => void {
    return (failure): void => {
      if (record.token !== active?.token
          && record.token !== activating?.token
          && record.token !== cleanupPending?.token) {
        return;
      }
      if (record.cleaning) {
        record.failures.push(failure);
        return;
      }
      if (activating === record) {
        record.failures.push(failure);
        return;
      }
      if (applying?.record === record) {
        applying.failures.push(failure);
        return;
      }
      if (framing?.record === record) {
        framing.failures.push(failure);
        return;
      }
      try {
        recoverActive(record, failure);
      } catch {
        const cleaned = cleanupRecord(record);
        setTerminal(
          record.adapter.kind,
          cleaned ? failure.phase : "destroy",
          cleaned ? failure.code : "cleanup",
          "Renderer host listener recovery failed",
          failure,
        );
      }
    };
  }

  function scheduleFrame(record: AdapterRecord): boolean {
    if (record.frameHandle !== null) return true;
    if (destroyRequested || terminalFailure !== null || cleanupPending !== null) return false;
    if (record.framePolicy !== "host-raf") return true;
    if (lifecyclePaused) return true;
    try {
      const generation = record.frameGeneration;
      record.frameHandle = requestFrame((nowMs): void => {
        if (record.frameGeneration !== generation || lifecyclePaused || terminalFailure !== null
            || cleanupPending !== null || destroyRequested || active !== record) return;
        record.frameHandle = null;
        const shouldRender = !lowPower
          || lastLowPowerFrameMs === null
          || nowMs - lastLowPowerFrameMs >= LOW_POWER_FRAME_INTERVAL_MS;
        if (shouldRender) {
          const operation: BufferedOperation = { record, failures: [] };
          framing = operation;
          let thrown: unknown;
          try {
            if (lowPower) lastLowPowerFrameMs = nowMs;
            onFrame?.(nowMs);
            record.adapter.frame(nowMs);
          } catch (cause) {
            thrown = cause;
          } finally {
            framing = null;
          }
          const failure = operation.failures[0]
            ?? rendererFailure(thrown)
            ?? (thrown === undefined ? null : Object.freeze({
              kind: record.adapter.kind,
              phase: "frame" as const,
              code: "resource" as const,
              message: "Renderer frame callback threw",
              recoverable: record.adapter.kind !== "dom",
            }));
          if (failure !== null) {
            try {
              recoverActive(record, failure);
            } catch {
              const cleaned = cleanupRecord(record);
              setTerminal(
                record.adapter.kind,
                cleaned ? "frame" : "destroy",
                cleaned ? "resource" : "cleanup",
                "Renderer host frame recovery failed",
                failure,
              );
            }
            return;
          }
        }
        if (active === record && !scheduleFrame(record)) {
          const cleaned = cleanupRecord(record);
          setTerminal(
            record.adapter.kind,
            cleaned ? "frame" : "destroy",
            cleaned ? "resource" : "cleanup",
            "Renderer host could not continue its frame subscription",
          );
        }
      });
      return true;
    } catch {
      return false;
    }
  }

  function activationFailure(kind: SceneRendererKind, cause: unknown): RendererFailure {
    return rendererFailure(cause) ?? Object.freeze({
      kind,
      phase: "initialize" as const,
      code: "initialization" as const,
      message: `${kind} renderer activation failed`,
      recoverable: kind !== "dom",
    });
  }

  function activate(kind: SceneRendererKind, plan: ScenePlan): boolean {
    if (destroyRequested || cleanupPending !== null || attemptedKinds.has(kind)) return false;
    attemptedKinds.add(kind);
    let adapter: SceneRenderer;
    try {
      adapter = factories[kind](root, plan);
    } catch (cause) {
      const failure = activationFailure(kind, cause);
      lastFailure = failure;
      const fallback = failure.recoverable ? nextFallback(kind) : null;
      if (fallback !== null) return activate(fallback, plan);
      setTerminal(kind, failure.phase, failure.code, "Renderer construction failed", failure);
      return false;
    }

    const record: AdapterRecord = {
      adapter,
      token: nextToken,
      failures: [],
      framePolicy: "none",
      unsubscribeFailure: null,
      frameHandle: null,
      frameGeneration: 0,
      cleaning: false,
    };
    nextToken += 1;
    activating = record;
    let thrown: unknown;
    try {
      record.unsubscribeFailure = adapter.onFailure(listenerFor(record));
      if (record.failures.length === 0) adapter.apply(plan);
      const diagnostics = safeDiagnostics(record);
      if (diagnostics !== null) record.framePolicy = diagnostics.framePolicy;
      if (record.failures.length === 0 && diagnostics?.signature !== plan.signature) {
        record.failures.push(Object.freeze({
          kind,
          phase: "apply",
          code: "apply",
          message: `${kind} renderer did not accept the complete ScenePlan`,
          recoverable: kind !== "dom",
        }));
      }
      if (record.failures.length === 0 && !scheduleFrame(record)) {
        record.failures.push(Object.freeze({
          kind,
          phase: "initialize",
          code: "resource",
          message: `${kind} renderer frame subscription failed`,
          recoverable: kind !== "dom",
        }));
      }
    } catch (cause) {
      thrown = cause;
    }

    const failure = record.failures[0] ?? (thrown === undefined ? null : activationFailure(kind, thrown));
    if (failure !== null) {
      lastFailure = failure;
      const cleaned = cleanupRecord(record);
      if (!cleaned) {
        setTerminal(kind, "destroy", "cleanup", "Renderer activation cleanup is incomplete", failure);
        return false;
      }
      const fallback = failure.recoverable ? nextFallback(kind) : null;
      if (fallback !== null) return activate(fallback, plan);
      setTerminal(kind, failure.phase, failure.code, "Renderer activation failed", failure);
      return false;
    }

    activating = null;
    active = record;
    terminalFailure = null;
    options.onKindChange?.(kind);
    return true;
  }

  function recoverActive(record: AdapterRecord, failure: RendererFailure): void {
    if (recovering || destroyRequested || active !== record || failure.kind !== record.adapter.kind) return;
    recovering = true;
    lastFailure = failure;
    try {
      const cleaned = cleanupRecord(record);
      if (!cleaned) {
        setTerminal(record.adapter.kind, "destroy", "cleanup", "Renderer cleanup is incomplete", failure);
        return;
      }
      const fallback = failure.recoverable ? nextFallback(record.adapter.kind) : null;
      if (fallback !== null && appliedPlan !== null && activate(fallback, appliedPlan)) return;
      setTerminal(
        record.adapter.kind,
        failure.phase,
        failure.code,
        failure.recoverable ? "Renderer fallback chain is exhausted" : "Renderer entered a terminal state",
        failure,
      );
    } finally {
      recovering = false;
    }
  }

  try {
    addLifecycleListeners();
    appliedPlan = preparePlan(options.scenePlan);
    activate(firstKind, appliedPlan);
  } catch {
    const listenerCleanupFailure = removeLifecycleListeners();
    const record = (active ?? cleanupPending ?? activating) as AdapterRecord | null;
    const adapterCleaned = record === null || cleanupRecord(record);
    const cleanupFailed = listenerCleanupFailure !== undefined || !adapterCleaned;
    setTerminal(
      record?.adapter.kind ?? null,
      "initialize",
      cleanupFailed ? "cleanup" : "initialization",
      cleanupFailed
        ? "Renderer host initialization cleanup was incomplete"
        : "Renderer host rejected its initial ScenePlan",
    );
  }

  return Object.freeze({
    apply(nextScenePlan: ScenePlan): void {
      if (destroyRequested || cleanupPending !== null || terminalFailure !== null) return;
      const record = active;
      if (record === null) return;
      let candidate: ScenePlan;
      try {
        candidate = preparePlan(nextScenePlan);
      } catch {
        const cleaned = cleanupRecord(record);
        setTerminal(
          record.adapter.kind,
          cleaned ? "apply" : "destroy",
          cleaned ? "apply" : "cleanup",
          "Renderer host rejected an invalid ScenePlan",
        );
        return;
      }

      const operation: BufferedOperation = { record, failures: [] };
      applying = operation;
      let thrown: unknown;
      try {
        record.adapter.apply(candidate);
      } catch (cause) {
        thrown = cause;
      } finally {
        applying = null;
      }
      let failure = operation.failures[0] ?? rendererFailure(thrown);
      if (failure === null && safeDiagnostics(record)?.signature !== candidate.signature) {
        failure = Object.freeze({
          kind: record.adapter.kind,
          phase: "apply",
          code: "apply",
          message: "Renderer did not commit the candidate ScenePlan",
          recoverable: false,
        });
      }
      if (failure === null) {
        appliedPlan = candidate;
        return;
      }

      const cleaned = cleanupRecord(record);
      if (!cleaned) {
        setTerminal(record.adapter.kind, "destroy", "cleanup", "Failed apply cleanup is incomplete", failure);
        return;
      }
      const fallback = failure.recoverable ? nextFallback(record.adapter.kind) : null;
      if (fallback !== null && appliedPlan !== null && activate(fallback, appliedPlan)) return;
      setTerminal(record.adapter.kind, failure.phase, failure.code, "Renderer apply failed", failure);
    },
    setReducedMotion(nextReducedMotion: boolean): void {
      if (destroyRequested || cleanupPending !== null || terminalFailure !== null
          || nextReducedMotion === reducedMotion) return;
      reducedMotion = nextReducedMotion;
      if (forcedDom) return;
      const targetKind = reducedMotion ? "dom" : baselineKind;
      if (active?.adapter.kind === targetKind || appliedPlan === null) return;
      const record = active;
      if (record !== null && !cleanupRecord(record)) {
        setTerminal(record.adapter.kind, "destroy", "cleanup", "Reduced-motion transition cleanup is incomplete");
        return;
      }
      attemptedKinds.clear();
      activate(targetKind, appliedPlan);
    },
  diagnostics(): RendererHostDiagnostics {
    let snapshotActive = active;
    let snapshotCleanupPending = cleanupPending;
    let snapshotTerminalFailure = terminalFailure;
    let snapshotDestroyed = destroyed;
    let record = snapshotActive;
    let resourceRecord = record ?? snapshotCleanupPending;
    let resources = resourceRecord === null ? null : safeDiagnostics(resourceRecord);
    const transitionedDuringDiagnostics = (): boolean =>
      active !== snapshotActive
      || cleanupPending !== snapshotCleanupPending
      || terminalFailure !== snapshotTerminalFailure
      || destroyed !== snapshotDestroyed;

    if (transitionedDuringDiagnostics()) {
      snapshotActive = active;
      snapshotCleanupPending = cleanupPending;
      snapshotTerminalFailure = terminalFailure;
      snapshotDestroyed = destroyed;
      record = snapshotActive;
      resourceRecord = record ?? snapshotCleanupPending;
      resources = resourceRecord === null ? null : safeDiagnostics(resourceRecord);

      if (transitionedDuringDiagnostics()) {
        record = active;
        resourceRecord = record ?? cleanupPending;
        resources = null;
      }
    }
    return Object.freeze({
        kind: record?.adapter.kind ?? null,
        signature: record === null ? null : resources?.signature ?? null,
        framePolicy: record === null ? "none" : resources?.framePolicy ?? "none",
        canvasCount: resources?.canvasCount ?? 0,
        activeSubscriptions: resourceRecord?.frameHandle === null || resourceRecord === null ? 0 : 1,
        activeAdapterCount: record === null ? 0 : 1,
        attemptedKinds: Object.freeze([...attemptedKinds]),
        lowPower,
        reducedMotion,
        cleanupPending: cleanupPending !== null,
        lastFailure,
        terminalFailure,
        destroyed,
        observation: record === null ? null : resources?.observation ?? null,
        retiredObservations: Object.freeze([...retiredObservations]),
      });
    },
    destroy(): void {
      if (destroyed) return;
      destroyRequested = true;
      const listenerCleanupFailure = removeLifecycleListeners();
      const record = active ?? cleanupPending ?? activating;
      const adapterCleaned = record === null || cleanupRecord(record);
      if (!adapterCleaned || listenerCleanupFailure !== undefined) {
        setTerminal(record?.adapter.kind ?? null, "destroy", "cleanup", "Renderer host destruction is incomplete");
        return;
      }
      if (!visibilityListenerAttached && !pageHideListenerAttached && !pageShowListenerAttached
          && !boundsListenerAttached && boundsObserver === null
          && active === null && activating === null && cleanupPending === null) {
        destroyed = true;
      }
    },
  });
}
